import React, { FC, useEffect, useMemo, useState } from 'react';
import { SelectValue } from 'antd/lib/select';
import url from 'url';
import qs, { StringifyOptions } from 'query-string';

import { Select, SamoletSelectProps } from 'ui/components/Select';

import { Filter } from 'components/FiltersBlock/types';

import { useQuery } from 'hooks';

import { Label, ReqLabel, Wrapper } from '../styled';

export const SelectFilter: FC<Props> = props => {
  const {
    label,
    apiUrl = '',
    name,
    customOptions,
    constantFilter,
    additionalField,
    dependsOn,
    localStorageKey,
    loadOptions = false,
    searchStringify,
    notReplaceQuery = false,
    queryStringifyOptions,
    // @ts-ignore
    required,
    onFilterChange,
    ...rest
  } = props;

  const [currentValue, setCurrentValue] = useState<SelectValue>();

  const { replaceQuery, query } = useQuery();

  // Reset Filter
  useEffect(() => {
    if (
      (!additionalField && !query[name]) ||
      (additionalField && !query[name] && !query[additionalField.name])
    ) {
      setCurrentValue([]);
      return;
    }
  }, [query, additionalField, name]);

  // Set select value when customOptions exist
  useEffect(() => {
    if (customOptions && query[name]) {
      const option = customOptions.find(el => String(el.value) === query[name]);

      if (!option) return;

      setCurrentValue(option.value);
      return;
    }
  }, [customOptions, name, query]);

  // set value from URL
  useEffect(() => {
    const selectValue = query[name];

    if (constantFilter && name === query[constantFilter.name]) {
      setCurrentValue(query[constantFilter.value] || undefined);
      return;
    }

    if ((selectValue || String(selectValue) === 'false') && !Array.isArray(selectValue)) {
      setCurrentValue(String(selectValue));
    }
  }, [query, constantFilter, name]);

  // Set value from URL && If mode === 'multiple'
  useEffect(() => {
    const selectValue = query[name];

    if (rest.mode === 'multiple' && selectValue) {
      if (Array.isArray(selectValue)) {
        setCurrentValue(selectValue.map(String));
      } else {
        if (queryStringifyOptions?.arrayFormat === 'comma') {
          setCurrentValue(selectValue.split(','));
        } else {
          setCurrentValue(selectValue);
        }
      }
    }
  }, [name, query, rest.mode, queryStringifyOptions?.arrayFormat]);

  // Set select value when additionalOption exist
  useEffect(() => {
    if (additionalField) {
      const selectValue = query[additionalField.name];

      if (selectValue && !Array.isArray(selectValue)) {
        setCurrentValue(additionalField.name);
      }
    }
  }, [additionalField, query]);

  const apiWithDep = useMemo(() => {
    if (dependsOn) {
      const dependsQuery = Array.isArray(dependsOn)
        ? dependsOn.reduce((acc, cur) => {
            const curFilter = query[cur] ? { [cur]: query[cur] } : {};
            return { ...acc, ...curFilter };
          }, {})
        : { [dependsOn]: query[dependsOn] };

      const parsedUrl = qs.parseUrl(apiUrl);

      return url.format({
        pathname: parsedUrl.url,
        search: qs.stringify({ ...parsedUrl.query, ...dependsQuery }, searchStringify),
      });
    }

    return apiUrl;
  }, [query, dependsOn, apiUrl, searchStringify]);

  const onChange = (value: SelectValue) => {
    onFilterChange && onFilterChange(value, name);

    const { page } = query;

    if (notReplaceQuery) {
      return;
    }

    if (constantFilter) {
      replaceQuery(
        {
          [constantFilter.name]: name,
          [constantFilter.value]: value,
        },
        // queryStringifyOptions,
      );
      setCurrentValue(value);
      return;
    }

    if (!additionalField) {
      replaceQuery(
        {
          [name]: value,
          page: page ? 1 : undefined,
        },
        // queryStringifyOptions,
      );
      setCurrentValue(value);
      return;
    }

    if (
      value === additionalField.name ||
      (Array.isArray(value) && value[value.length - 1] === additionalField.name)
    ) {
      replaceQuery(
        {
          [name]: undefined,
          [additionalField.name]: true,
          page: page ? 1 : undefined,
        },
        // queryStringifyOptions,
      );
    } else {
      if (Array.isArray(value)) {
        const newArr = (value as string[]).filter(el => el !== additionalField.name);

        replaceQuery(
          {
            [name]: newArr,
            [additionalField.name]: undefined,
            page: page ? 1 : undefined,
          },
          // queryStringifyOptions,
        );

        setCurrentValue(newArr);
        return;
      }

      replaceQuery(
        {
          [name]: value,
          [additionalField.name]: undefined,
          page: page ? 1 : undefined,
        },
        // queryStringifyOptions,
      );
    }

    setCurrentValue(value);
  };

  return (
    <Wrapper>
      <Label>
        {label} {required ? <ReqLabel>*</ReqLabel> : null}
      </Label>

      <Select
        name={name}
        value={currentValue}
        apiUrl={apiWithDep}
        additionalField={additionalField}
        placeholder='Выберите'
        size='small'
        allowClear
        loadOptions={loadOptions}
        customOptions={customOptions}
        localStorageKey={localStorageKey}
        getPopupContainer={(trigger: HTMLElement) => trigger.parentElement || document.body}
        valueToString
        onChange={onChange}
        onManualSetValue={onChange}
        {...rest}
      />
    </Wrapper>
  );
};

interface Props
  extends Omit<SamoletSelectProps, 'label' | 'name'>,
    Omit<Filter, 'onBlur' | 'placeholder' | 'type'> {
  customOptions?: Option[];
  onFilterChange: (value: SelectValue, name: string) => void;
  queryStringifyOptions?: StringifyOptions;
}

type Option = { value: number | string; label: string };
